import { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useRoom } from '../hooks/useRoom';
import { useMatrixClient } from './MatrixContext';
import { isServerNoticesRoom } from '../utils/roomUtils';

const RoomContext = createContext();


/**
 * Custom hook to access the room context.
 * @returns {object} The value from the RoomContext, which includes all data from the useRoom hook.
 * Returns null if used outside a provider (e.g., in NewChatView).
 */
export const useRoomContext = () => {
  const context = useContext(RoomContext);
  return context || null;
};

/**
 * Provides room-specific data to all descendant components.
 * It fetches data using the useRoom hook and makes it available through context.
 * @param {object} props - The component props.
 * @param {string} props.roomId - The ID of the room to provide data for.
 * @param {React.ReactNode} props.children - The child components.
 */
export const RoomProvider = ({ children }) => {
  const { id: roomId } = useParams();
  const client = useMatrixClient();
  
  // Use the useRoom hook to get all room data
  // Note: members array contains enriched member objects with both 'id' and 'userId'
  // properties (same value) for compatibility with different components
  const {
    room,
    name,
    topic,
    members,
    roomAvatars,
    messageGroups,
    readReceipts,
    canEdit,
    sendMessage,
    cancelPendingMessage,
    sendAttachment,
    toggleReaction,
    deleteMessage,
    canDeleteMessage,
    reportMessage,
    powerLevelsCounter,
    isRoomLoading,
    roomExists,
    isEncrypted,
    isPublic,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
    firstUnreadEventId,
    hasUnread,
  } = useRoom(roomId);

  // roomAvatars now comes from useRoom hook - no need to recompute
  // Check if this is a server notices room
  // Only compute when room or client changes
  const isServerNotices = useMemo(
    () => isServerNoticesRoom(room, client),
    [room, client],
  );
  
  // Leave room function
  const leaveRoom = useCallback(async () => {
    if (!client || !roomId) throw new Error('Cannot leave room');
    await client.leave(roomId);
  }, [client, roomId]);

  const value = useMemo(() => ({
    room,
    client,
    roomId,
    name,
    topic,
    members,
    roomAvatars,
    canEdit,
    messageGroups,
    readReceipts,
    isRoomLoading,
    roomExists,
    isEncrypted,
    isPublic,
    isServerNotices,
    leaveRoom,
    sendMessage,
    cancelPendingMessage,
    sendAttachment,
    toggleReaction,
    deleteMessage,
    reportMessage,
    canDeleteMessage,
    powerLevelsCounter,
    // Timeline pagination controls
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    readMarkerId,
    setReadMarker,
    // Unread tracking
    unreadCount,
    firstUnreadEventId,
    hasUnread,
  }), [
    room,
    client,
    roomId,
    name,
    topic,
    members,
    roomAvatars,
    canEdit,
    messageGroups,
    readReceipts,
    isRoomLoading,
    roomExists,
    isEncrypted,
    isPublic,
    isServerNotices,
    leaveRoom,
    sendMessage,
    cancelPendingMessage,
    sendAttachment,
    toggleReaction,
    deleteMessage,
    reportMessage,
    canDeleteMessage,
    powerLevelsCounter,
    // Timeline pagination dependencies
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    jumpToEvent,
    readMarkerId,
    setReadMarker,
    // Unread tracking dependencies
    unreadCount,
    firstUnreadEventId,
    hasUnread,
  ]);

  return (
    <RoomContext.Provider value={value}>
      {children}
    </RoomContext.Provider>
  );
};

RoomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RoomContext;
